'use client';
import { usePathname } from 'next/navigation';
import { classNames } from '@repo/ui/src/utils/classNames';
import type { MeaterProductVariant } from '@src/domain/types/MeaterProduct';
import { findBestVariant } from './_variantUtils';

interface GeneralVariantSelectorProps {
  optionName: string;
  variants: Array<MeaterProductVariant>;
  activeVariantId?: string;
}

const GeneralVariantSelector = ({
  optionName,
  variants,
  activeVariantId,
}: GeneralVariantSelectorProps) => {
  const pathname = usePathname();

  const activeVariant = variants.find((v) => v.id === activeVariantId);

  const optionVariants = variants
    .reduce<Array<{ label: string; isAvailable: boolean; href: string }>>((acc, v) => {
      const opt = v.selectedOptions.find((o) => o.name.toLowerCase() === optionName.toLowerCase());
      if (!opt || acc.some((item) => item.label === opt.value)) return acc;
      const target = findBestVariant(variants, activeVariant, optionName, opt.value) ?? v;
      return [
        ...acc,
        {
          label: opt.value,
          isAvailable: target.availableForSale,
          href: `${pathname}?variant=${target.variantId}`,
        },
      ];
    }, [])
    .sort((a, b) => Number(b.isAvailable) - Number(a.isAvailable));

  if (optionVariants.length <= 1) return null;

  const activeValue =
    activeVariant?.selectedOptions.find((o) => o.name.toLowerCase() === optionName.toLowerCase())
      ?.value ?? '';

  return (
    <div>
      <p className="tra-text-web-utl-link">{optionName}:</p>
      <div className="pt-2 flex flex-wrap gap-4">
        {optionVariants.map(({ label, isAvailable, href }) => (
          <a
            key={label}
            href={href}
            aria-current={label === activeValue || undefined}
            aria-disabled={!isAvailable || undefined}
            className={classNames(
              'tra-text-web-utl-link border px-3 py-1',
              label === activeValue
                ? 'border-brand-300 font-semibold text-brand-300'
                : 'border-neutral-300',
              !isAvailable && 'opacity-40 line-through',
            )}
          >
            {label}
          </a>
        ))}
      </div>
    </div>
  );
};

export default GeneralVariantSelector;
